import { Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Label, TextField } from '@/components';
import { NEW_FEATURE_ALERTS } from '@/constants';
import { useForm, useNewFeatureAlert } from '@/hooks';
import { AnalyticsService } from '@/services';
import {
  selectSessionUserId,
  selectUserCompliance,
  uiActions,
  useSendEpisodeShareEmailMutation,
} from '@/store';
import { Referrer } from '@/types';
import { SnackbarUtils } from '@/utils';

export type ShareEpisodeFormState = {
  firstName: string;
  lastName: string;
  email: string;
};

export const ShareEpisodeForm = ({
  episodeId,
  episodeDeeplink,
  referrer,
}: {
  episodeId: string;
  episodeDeeplink: string;
  referrer: Referrer;
}) => {
  const dispatch = useDispatch();
  const compliance = useSelector(selectUserCompliance);
  const userId = useSelector(selectSessionUserId);

  const [sendEpisodeShareEmail, { isLoading }] =
    useSendEpisodeShareEmailMutation();

  const { form, errors, validateForm, onChange } =
    useForm<ShareEpisodeFormState>({
      firstName: '',
      lastName: '',
      email: '',
    });

  const newFeatureAlert = useNewFeatureAlert(
    NEW_FEATURE_ALERTS.SHARE_EPISODE_MODAL_EMAIL,
  );

  const shareLink = `${episodeDeeplink}?utm_source=user_share&utm_medium=referral&utm_content=${episodeId}&ref_code=${userId}`;

  // const onMaybeAdvanceOnboardingChecklist = () => {
  //   const currentStep = userUIStates?.onboardingChecklistProgress?.currentStep;

  //   if (currentStep !== OnboardingChecklistStepOrder.SHARE_EPISODE) return;

  //   // Advance the step after a delay so that the animation plays after the share modal closes
  //   return setTimeout(
  //     () =>
  //       updateUserUIState({
  //         key: 'onboardingChecklistProgress',
  //         value: {
  //           isChecklistCompleted: false,
  //           currentStep: currentStep + 1,
  //         },
  //       }),
  //     500,
  //   );
  // };

  const onLinkCopy = () => {
    navigator.clipboard.writeText(shareLink);
    // onMaybeAdvanceOnboardingChecklist();
    SnackbarUtils.success('Link copied to clipboard');
    AnalyticsService.episodeShareCopy({ episodeId, location: referrer });
    dispatch(uiActions.closeActiveModal());
  };

  const onSendEmail = async () => {
    const isValid = validateForm(['firstName', 'lastName', 'email']);
    if (!isValid) return;

    try {
      newFeatureAlert.dismiss();
      await sendEpisodeShareEmail({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        episodeId,
        compliance,
      }).unwrap();
      // onMaybeAdvanceOnboardingChecklist();
      SnackbarUtils.success(`Email sent to ${form.firstName} ${form.lastName}`);
      dispatch(uiActions.closeActiveModal());
    } catch {
      SnackbarUtils.error(
        'Something went wrong. Please try again or contact support.',
      );
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
          gap: 1,
          alignItems: 'flex-end',
          mb: 3,
        }}>
        <TextField
          fullWidth
          label="Share Link"
          value={shareLink}
          sx={{ flexGrow: 1 }}
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
        />
        <Button
          label="Copy Link"
          skinny={false}
          sx={{ flex: 1, minWidth: 120 }}
          onClick={onLinkCopy}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Typography variant="h4">Send by Email</Typography>
        {newFeatureAlert.isVisible && <Label text="New" />}
      </Box>
      <Box
        sx={{
          gap: 1,
          display: 'flex',
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
        }}>
        <TextField
          id="firstName"
          fullWidth
          label="First Name"
          value={form.firstName}
          error={!!errors.firstName}
          helperText={errors.firstName}
          onChange={onChange}
        />
        <TextField
          id="lastName"
          fullWidth
          label="Last Name"
          value={form.lastName}
          error={!!errors.lastName}
          helperText={errors.lastName}
          onChange={onChange}
        />
      </Box>
      <TextField
        id="email"
        fullWidth
        label="Email"
        value={form.email}
        error={!!errors.email}
        helperText={errors.email}
        onChange={onChange}
      />
      <Button
        variant="primary"
        label="Share Episode"
        skinny={false}
        onClick={onSendEmail}
        fullWidth={false}
        isLoading={isLoading}
        sx={{ mt: 2 }}
      />
    </>
  );
};
